

import React, { useState, useMemo, useRef } from 'react';
import type { View } from '../types';
import { clinicalGuidesData } from '../data/clinicalGuidesData';
import SEO from './SEO';

// --- ICONS ---
const SmallLogoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="5" fill="#FACC15"/>
    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="7" r="2" fill="white"/>
    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="41" r="2" fill="white"/>
    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="7" cy="24" r="2" fill="white"/>
    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="41" cy="24" r="2" fill="white"/>
  </svg>
);
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const SearchIcon: React.FC = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const ChevronDownIcon: React.FC<{isOpen: boolean}> = ({isOpen}) => <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;

// Category Icons
const iconMap: { [key: string]: React.ReactNode } = {
    Cardiology: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>,
    Respiratory: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
    Endocrinology: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>,
    Rheumatology: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.52 19c.64-2.2 1.84-3 3.22-3h6.52c1.38 0 2.58.8 3.22 3" /></svg>,
    Neurology: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547a2 2 0 00-.547 1.806l.477 2.387a6 6 0 00.517 3.86l.158.318a6 6 0 00.517 3.86l2.387.477a2 2 0 001.806-.547a2 2 0 00.547-1.806l-.477-2.387a6 6 0 00-.517-3.86l-.158-.318a6 6 0 00-.517-3.86l-2.387-.477" /></svg>,
    Psychiatry: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
    InfectiousDisease: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 21h4a2 2 0 002-2V7a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
};


interface ClinicalGuidesProps {
    onNavigate: (view: View) => void;
    onSelectTopic: (topic: string, source: string) => void;
}

const ClinicalGuides: React.FC<ClinicalGuidesProps> = ({ onNavigate, onSelectTopic }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [openCategory, setOpenCategory] = useState<string | null>(clinicalGuidesData[0]?.id || null);
    const inputRef = useRef<HTMLInputElement>(null);

    const filteredData = useMemo(() => {
        if (!searchTerm) return clinicalGuidesData;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return clinicalGuidesData
            .map(category => ({
                ...category,
                topics: category.topics.filter(topic => topic.name.toLowerCase().includes(lowerCaseSearch))
            }))
            .filter(category => category.topics.length > 0 || category.name.toLowerCase().includes(lowerCaseSearch));
    }, [searchTerm]);

    const handleCategoryClick = (categoryId: string) => {
        setOpenCategory(prev => (prev === categoryId ? null : categoryId));
    };

    // Prepare Schema.org Structured Data for MedicalGuidelines
    const guidelinesSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": clinicalGuidesData.flatMap(cat => 
            cat.topics.map((topic, index) => ({
                "@type": "MedicalGuideline",
                "name": topic.name,
                "evidenceOrigin": topic.source,
                "medicalSpecialty": cat.name
            }))
        )
    };
    
    return (
         <div className="py-6">
            <SEO 
                title="Clinical Guidelines - Dr.Rx"
                description="Evidence-based summaries of major clinical practice guidelines including ACC/AHA, ADA, IDSA, and more."
                url="/?view=clinical-guides"
                keywords={["Clinical Practice Guidelines", "Evidence Based Medicine", "Medical Protocols", "Cardiology Guidelines", "IDSA", "ADA"]}
                schema={guidelinesSchema}
            />

            <header className="mb-8">
                 <div className="flex justify-between items-start mb-4">
                    <button onClick={() => onNavigate('library')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold">
                        <BackIcon /> Back to Library
                    </button>
                    <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-all cursor-pointer hover:scale-105">
                         <SmallLogoIcon />
                         <span className="text-sm font-bold text-white">Dr.<span className="text-yellow-400">Rx</span><sup className="text-xs -top-2">TM</sup></span>
                    </button>
                </div>
                <h1 className="text-4xl font-bold text-white relative inline-block">
                    Clinical Guides
                    <span className="absolute -top-1 -right-8 text-xs font-bold text-yellow-400 bg-yellow-900/50 border border-yellow-700 px-2 py-0.5 rounded-full">EVIDENCE-BASED</span>
                </h1>
                <p className="text-gray-400 mt-2 max-w-2xl">Quick access to summaries of major clinical practice guidelines from authoritative sources.</p>
            </header>

            <div className="sticky top-0 bg-[#101D35]/90 backdrop-blur-lg z-10 py-4 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-y border-blue-900/50">
                 <div className="relative group">
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search clinical topics..."
                        className="w-full bg-[#1E2E4A] border border-[#3A4A67] rounded-full p-3 pl-6 pr-14 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all"
                    />
                    <button 
                        onClick={() => inputRef.current?.focus()}
                        className={`absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all duration-300 ${searchTerm ? 'text-yellow-400 bg-yellow-400/10 scale-110 shadow-[0_0_10px_rgba(250,204,21,0.3)]' : 'text-gray-500 group-hover:text-gray-300'}`}
                    >
                        <SearchIcon />
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {filteredData.map(category => (
                    <div key={category.id} className="bg-[#15243E] rounded-xl border border-blue-900/50 overflow-hidden">
                        <button onClick={() => handleCategoryClick(category.id)} className="w-full flex items-center justify-between p-4 text-left">
                            <div className="flex items-center gap-4">
                                <span className="text-yellow-400">{iconMap[category.icon] || '•'}</span>
                                <h2 className="text-xl font-bold text-white">{category.name}</h2>
                            </div>
                            <ChevronDownIcon isOpen={openCategory === category.id} />
                        </button>
                        {openCategory === category.id && (
                            <div className="p-4 border-t border-blue-900/50 grid grid-cols-1 md:grid-cols-2 gap-4">
                                {category.topics.map(topic => (
                                    <button 
                                        key={topic.name} 
                                        onClick={() => onSelectTopic(topic.name, topic.source)}
                                        className="text-left p-4 bg-black/20 rounded-lg hover:bg-yellow-400/10 transition-colors group"
                                    >
                                        <p className="font-semibold text-gray-200 group-hover:text-yellow-300">{topic.name}</p>
                                        <p className="text-xs text-gray-500">Source: {topic.source}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                {filteredData.length === 0 && (
                     <div className="text-center py-16 text-gray-400">
                        <p className="text-lg font-semibold">No results found</p>
                        <p>Try adjusting your search term.</p>
                    </div>
                )}
            </div>

         </div>
    );
};

export default ClinicalGuides;